import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import { ArrowLeft, GitCompare, Loader2 } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { runProjection } from "@/lib/api";
import { fmtCurrency, fmtCurrencyFull, fmtPct } from "@/lib/format";
import { decodePlan } from "@/lib/shareUrl";
import { secureGetJSON } from "@/lib/secureStorage";
import useSEO from "@/lib/useSEO";

const STORAGE_KEY = "centennial_scenarios_v1";

const DEFAULT_INPUTS = {
  current_age: 32,
  retirement_age: 65,
  life_expectancy: 100,
  current_savings: 50000,
  monthly_contribution: 1500,
  inflation_rate: 2.5,
  annual_withdrawal: 60000,
  allocation: { stocks: 70, bonds: 25, cash: 5 },
  simulations: 1500,
  withdrawal_strategy: "fixed_real",
  withdrawal_percentage: 4.0,
  guardrails: null,
  income_streams: [],
  tax_config: null,
  healthcare: null,
  real_estate: null,
};

// Diff row helper
const DiffRow = ({ label, a, b, formatter = (v) => v, betterDirection = "higher" }) => {
  const aN = Number(a);
  const bN = Number(b);
  const diff = bN - aN;
  const betterB = betterDirection === "higher" ? diff > 0 : diff < 0;
  const cls = diff === 0 ? "text-[#5C6B62]" : betterB ? "text-[#348C59]" : "text-[#C26747]";
  return (
    <div className="grid grid-cols-12 items-baseline border-b border-[#E5E2DC] py-3 last:border-0">
      <div className="col-span-4 text-sm text-[#5C6B62]">{label}</div>
      <div className="col-span-3 text-right font-mono-num text-sm text-[#1C2B23]">{formatter(aN)}</div>
      <div className="col-span-3 text-right font-mono-num text-sm text-[#1C2B23]">{formatter(bN)}</div>
      <div className={`col-span-2 text-right font-mono-num text-sm ${cls}`}>
        {diff > 0 ? "+" : ""}
        {formatter(diff)}
      </div>
    </div>
  );
};

export default function Compare() {
  useSEO({
    title: "Compare Two Retirement Plans Side-by-Side · Centennial",
    description: "Run two retirement scenarios head-to-head with live Monte Carlo simulations. See success probability, median balance, and downside (P10) tradeoffs at a glance. Free, no signup.",
    canonicalPath: "/compare",
  });
  const [scenarios, setScenarios] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load saved scenarios + current plan (from URL hash if present)
  useEffect(() => {
    const list = secureGetJSON(STORAGE_KEY, null);
    if (list) setScenarios(list);
    const shared = decodePlan(window.location.hash);
    setCurrentPlan(shared ? { ...DEFAULT_INPUTS, ...shared } : DEFAULT_INPUTS);
  }, []);

  const options = useMemo(() => {
    const base = [{ id: "__current__", name: "Current plan", inputs: currentPlan }];
    return base.concat(scenarios.map((s) => ({ id: s.id, name: s.name, inputs: s.inputs })));
  }, [scenarios, currentPlan]);

  // Auto-select two distinct options on mount
  useEffect(() => {
    if (options.length >= 1 && !aId) setAId(options[0].id);
    if (options.length >= 2 && !bId) setBId(options[1].id);
  }, [options.length]); // eslint-disable-line

  const planFor = (id) => options.find((o) => o.id === id)?.inputs;

  const runBoth = async () => {
    const a = planFor(aId);
    const b = planFor(bId);
    if (!a || !b) return;
    setLoading(true);
    try {
      const [rA, rB] = await Promise.all([runProjection(a), runProjection(b)]);
      setResultA(rA);
      setResultB(rB);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // auto-run when both ids set and current plan loaded
  useEffect(() => {
    if (aId && bId && currentPlan) runBoth();
    // eslint-disable-next-line
  }, [aId, bId, currentPlan]);

  const chartData = useMemo(() => {
    if (!resultA || !resultB) return [];
    const byAge = {};
    resultA.monte_carlo.forEach((p) => {
      byAge[p.age] = { age: p.age, a_p50: p.p50, a_p10: p.p10, a_p90: p.p90 };
    });
    resultB.monte_carlo.forEach((p) => {
      byAge[p.age] = { ...(byAge[p.age] || { age: p.age }), b_p50: p.p50, b_p10: p.p10, b_p90: p.p90 };
    });
    return Object.values(byAge).sort((a, b) => a.age - b.age);
  }, [resultA, resultB]);

  const planA = planFor(aId);
  const planB = planFor(bId);
  const nameA = options.find((o) => o.id === aId)?.name ?? "Plan A";
  const nameB = options.find((o) => o.id === bId)?.name ?? "Plan B";

  return (
    <div data-testid="compare-page" className="min-h-screen bg-[#F9F8F6] text-[#1C2B23]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <Link
            data-testid="back-home"
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#1C2B23] hover:text-[#2B5339]"
          >
            <ArrowLeft size={16} /> Back to calculator
          </Link>
          <div className="font-display text-[#1C2B23] tracking-tight text-lg">
            Centennial<span className="text-[#C26747]">.</span>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-12 lg:py-16">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
          side-by-side
        </div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl tracking-tight font-medium">
          Compare two plans.
        </h1>
        <p className="mt-3 text-[#5C6B62] max-w-2xl">
          Pick two scenarios — the current plan, or anything you've saved — and see them run head-to-head.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-2">
              Plan A
            </div>
            <Select value={aId} onValueChange={setAId}>
              <SelectTrigger data-testid="select-plan-a" className="h-11">
                <SelectValue placeholder="choose…" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {planA && (
              <div className="mt-3 text-xs text-[#5C6B62] grid grid-cols-2 gap-y-1 gap-x-3 font-mono-num">
                <span>age {planA.current_age} → {planA.retirement_age} → {planA.life_expectancy}</span>
                <span>{fmtCurrencyFull(planA.current_savings)} saved</span>
                <span>{fmtCurrencyFull(planA.monthly_contribution)}/mo</span>
                <span>{fmtCurrencyFull(planA.annual_withdrawal)}/yr</span>
              </div>
            )}
          </div>
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-5">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-2">
              Plan B
            </div>
            <Select value={bId} onValueChange={setBId}>
              <SelectTrigger data-testid="select-plan-b" className="h-11">
                <SelectValue placeholder="choose…" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {planB && (
              <div className="mt-3 text-xs text-[#5C6B62] grid grid-cols-2 gap-y-1 gap-x-3 font-mono-num">
                <span>age {planB.current_age} → {planB.retirement_age} → {planB.life_expectancy}</span>
                <span>{fmtCurrencyFull(planB.current_savings)} saved</span>
                <span>{fmtCurrencyFull(planB.monthly_contribution)}/mo</span>
                <span>{fmtCurrencyFull(planB.annual_withdrawal)}/yr</span>
              </div>
            )}
          </div>
        </div>

        {/* Chart overlay */}
        <div className="mt-8 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
                <GitCompare size={12} /> head-to-head
              </div>
              <div className="font-display text-lg text-[#1C2B23] mt-1">
                Median portfolio paths — real $
              </div>
            </div>
            {loading && <Loader2 className="animate-spin text-[#5C6B62]" size={14} />}
          </div>
          <div className="h-[400px]" style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <ComposedChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="age" tick={{ fill: "#5C6B62", fontSize: 11 }} axisLine={{ stroke: "#E5E2DC" }} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtCurrency(v)} tick={{ fill: "#5C6B62", fontSize: 11 }} axisLine={false} tickLine={false} width={70} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E5E2DC", borderRadius: 12, fontSize: 11 }}
                  formatter={(v, name) => [fmtCurrencyFull(v), name]}
                  labelFormatter={(l) => `age ${l}`}
                />
                <Legend />
                {planA && <ReferenceLine x={planA.retirement_age} stroke="#C26747" strokeDasharray="4 4" />}
                <Line name={`${nameA} · p50`} type="monotone" dataKey="a_p50" stroke="#2B5339" strokeWidth={2.4} dot={false} isAnimationActive={false} />
                <Line name={`${nameB} · p50`} type="monotone" dataKey="b_p50" stroke="#C26747" strokeWidth={2.4} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Diff table */}
        {resultA && resultB && (
          <div data-testid="diff-table" className="mt-6 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
            <div className="font-display text-lg text-[#1C2B23] mb-4">Side-by-side metrics</div>
            <div className="grid grid-cols-12 items-baseline border-b-2 border-[#E5E2DC] pb-2 mb-1 text-[10px] tracking-[0.18em] uppercase text-[#5C6B62] font-semibold">
              <div className="col-span-4">Metric</div>
              <div className="col-span-3 text-right">{nameA}</div>
              <div className="col-span-3 text-right">{nameB}</div>
              <div className="col-span-2 text-right">B − A</div>
            </div>
            <DiffRow
              label="Success probability"
              a={resultA.summary.success_probability_pct}
              b={resultB.summary.success_probability_pct}
              formatter={(v) => fmtPct(v, 1)}
            />
            <DiffRow
              label="Median final balance"
              a={resultA.summary.median_final_balance}
              b={resultB.summary.median_final_balance}
              formatter={fmtCurrencyFull}
            />
            <DiffRow
              label="P10 final balance"
              a={resultA.summary.p10_final_balance}
              b={resultB.summary.p10_final_balance}
              formatter={fmtCurrencyFull}
            />
            <DiffRow
              label="P90 final balance"
              a={resultA.summary.p90_final_balance}
              b={resultB.summary.p90_final_balance}
              formatter={fmtCurrencyFull}
            />
            <DiffRow
              label="Years projected"
              a={resultA.summary.years_projected}
              b={resultB.summary.years_projected}
              formatter={(v) => `${v} yrs`}
            />
          </div>
        )}
      </section>
    </div>
  );
}
